import { read_portal_theme_mode, is_public_theme_route } from './theme_mode';
import createToolkitTheme from '@/uptk/core/packages/theme/createToolkitTheme';
import {
  create_toolkit_palette_overrides,
  default_tenant_theme_configuration,
  resolve_theme_mode_configuration,
} from '@/uptk/core/theme/theme_runtime';

type AuthThemeMode = 'light' | 'dark';

const is_theme_mode = (value: unknown): value is AuthThemeMode => value === 'light' || value === 'dark';

const resolve_stored_theme_mode = (): AuthThemeMode | null => {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const selectedTheme = typeof window !== 'undefined' && !is_public_theme_route(window.location.pathname) ? read_portal_theme_mode() : null;
  if (is_theme_mode(selectedTheme)) {
    return selectedTheme;
  }

  let publicTheme: string | null = null;
  try { publicTheme = localStorage.getItem('publicTheme'); } catch { return null; }
  if (is_theme_mode(publicTheme)) {
    return publicTheme;
  }

  return null;
};

export const resolve_auth_theme_mode = (...candidates: unknown[]): AuthThemeMode => {
  for (const candidate of candidates) {
    if (is_theme_mode(candidate)) {
      return candidate;
    }
  }

  return resolve_stored_theme_mode() ?? 'light';
};

const create_fallback_theme = (mode: AuthThemeMode) => {
  const resolvedTheme = resolve_theme_mode_configuration(
    default_tenant_theme_configuration,
    'portal',
    mode
  );

  return createToolkitTheme(mode, undefined, undefined, {
    palette: create_toolkit_palette_overrides(resolvedTheme),
  });
};

const fallback_theme_by_mode = {
  light: create_fallback_theme('light'),
  dark: create_fallback_theme('dark'),
};

export const get_default_auth_theme = (preferredMode?: unknown) =>
  fallback_theme_by_mode[resolve_auth_theme_mode(preferredMode)];

export const normalize_auth_theme = (theme: Record<string, unknown> | undefined, preferredMode?: unknown) => {
  const mode = resolve_auth_theme_mode(theme?.name, preferredMode);
  const fallbackTheme = fallback_theme_by_mode[mode];

  if (!theme || typeof theme !== 'object') {
    return fallbackTheme;
  }

  return {
    ...fallbackTheme,
    ...theme,
    name: mode,
    palette: {
      ...(fallbackTheme.palette as Record<string, unknown>),
      ...((theme.palette as Record<string, unknown>) ?? {}),
    },
  };
};

export const initialize_auth_theme = <T extends Record<string, unknown> | null | undefined>(auth: T): T => {
  if (!auth || typeof auth !== 'object') {
    return auth;
  }

  (auth as Record<string, unknown>).theme = normalize_auth_theme(auth.theme as Record<string, unknown> | undefined);
  return auth;
};
